/* eslint-disable no-useless-escape */

import dayjs from "dayjs";

import { StepDef, WizardDefinition } from "@/app/wizard/wizard-types";
import {
  Couple,
  Family,
  Group,
  Individual,
  OptimizelyExperimentsEnum,
} from "@/config/app-constant";
import {
  PRODUCT,
  SECONDARY_PRODUCT,
  MEDICAL_FLOW_V2_ENABLED,
  MAX_ADDRESS_LENGTH,
  ANNUAL_MULTI_TRIP,
} from "@/constants";
import { AddressType } from "@/types/AddressTypes";
import { Organiser } from "@/types/Organiser";
import { FormDataTypeOrganiser } from "@/types/OrganiserType";
import { Traveller } from "@/types/Traveller";
import { FormDataType } from "@/types/TravellerType";

import { OrganiserContent } from "./schema/organiser-page-schema.utils";
import { isValidDate, stripTimeFromDate } from "./travel-date.utils";
import { validateName } from "./traveller.utils";

const ageLimit = {
  avanti: { min: 18, max: 120 },
  staysure: { min: 18, max: 120 },
  expat: { min: 18, max: 85 },
};

const brandImpact = {
  avanti: false,
  staysure: false,
  expat: true,
};

const sendTargetCountry = {
  avanti: false,
  staysure: false,
  expat: true,
};

const brand: "staysure" | "avanti" | "expat" =
  SECONDARY_PRODUCT === "expat"
    ? "expat"
    : (PRODUCT as "staysure" | "avanti") ?? "staysure";

export const isAvailableNationalityField = brandImpact[brand];
export const ageLimitOrganizer = ageLimit[brand];
export const sendTargetCountryToAddressService = sendTargetCountry[brand];
export const allowTownFieldInAddress = brandImpact[brand];

//Get the Spain A/B test variant value from localStorage
export const getPersonalDetailsVariant = (): string | null => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return localStorage.getItem(
    OptimizelyExperimentsEnum.HIDE_ORGANISER_PERSONAL_DETAILS
  );
};

export const hidePersonalDetailsFields = (
  experimentDetails?: {
    deferOrganiserDetailsFlow?: string | null;
  } | null
): boolean => {
  // Check backend response if user comming from email & hub
  if (experimentDetails?.deferOrganiserDetailsFlow === "variant") return true;
  if (experimentDetails?.deferOrganiserDetailsFlow === "control") return false;

  // Fallback to localStorage for new users (Optimizely assignment)
  return getPersonalDetailsVariant() === "variant";
};

export const answersQuestionYesNo = (
  t: (key: string, fallback: string) => string
) => [
  {
    id: 1,
    label: t("button_group_yes", "Yes"),
    value: true,
  },
  {
    id: 2,
    label: t("button_group_no", "No"),
    value: false,
  },
];

export function findAnswerQThreeById(
  id: number,
  t: (key: string, fallback: string) => string
) {
  return answersQuestionYesNo(t).find((answer) => answer.id === id);
}

export function findAnswerQThreeByValue(
  value: boolean | undefined,
  t: (key: string, fallback: string) => string
) {
  return answersQuestionYesNo(t).find((answer) => answer.value === value);
}

export function getOrganiserDateValidation(
  form: FormDataType,
  travellerType: string,
  dobMaxLimit: string,
  dobMinAge: string,
  dobMaxAge: string,
  current?: StepDef
) {
  let errorMessage = "";
  if (form.value4) {
    if (Number(form.value4) < 0 || Number(form.value4) === -0.5) {
      errorMessage = dobMinAge;
    } else if (
      Number(form.value4) > ageLimitOrganizer.max ||
      (current?.brandSpecificParams &&
        Number(form.value4) > Number(current?.brandSpecificParams?.ageLimitMax))
    ) {
      errorMessage = dobMaxAge;
    } else if (
      Number(form.value4) < ageLimitOrganizer.min ||
      (current?.brandSpecificParams &&
        Number(form.value4) < Number(current?.brandSpecificParams?.ageLimitMin))
    ) {
      errorMessage = dobMaxLimit;
    }
  }
  if (!isValidDate(form.dateValue as Date)) {
    errorMessage = "Invalid Date of Birth";
  }

  // else if (travellerType === Family) {
  //   if (Number(form.value4) > 17 && Number(form.value4) < 21) {
  //     if (form.fullTimeEducation) {
  //       errorMessage =
  //         ageFamilyFullTimeEducation ||
  //         "A family must be 2 adults plus any number of children under 18, or under 21 if in full time education";
  //     }
  //   }
  // } else if (travellerType === SingleParentFamily) {
  //   if (Number(form.value4) > 17 && Number(form.value4) < 21) {
  //     if (form.fullTimeEducation) {
  //       errorMessage =
  //         ageSingleParentFullTimeEducation ||
  //         "Single Parent travel  must be 1 adults plus any number of children under 18, or under 21 if in full time education";
  //     }
  //   }
  // }
  return errorMessage;
}

export function getOrganiserDateValidationStates(
  form: FormDataType,
  current?: StepDef
) {
  let errorState = false;
  if (form.dateValue && isValidDate(form.dateValue) && form.value4) {
    if (
      Number(form.value4) < 0 ||
      (current?.brandSpecificParams &&
        (Number(form.value4) <
          Number(current?.brandSpecificParams?.ageLimitMin) ||
          Number(form.value4) >
            Number(current?.brandSpecificParams?.ageLimitMax))) ||
      Number(form.value4) > ageLimitOrganizer.max ||
      Number(form.value4) < ageLimitOrganizer.min
    ) {
      errorState = true;
    }

    // else if (travellerType === Family) {
    //   if (Number(form.value4) > 17 && Number(form.value4) < 21) {
    //     if (form.fullTimeEducation) {
    //       errorState = true;
    //     }
    //   }
    // } else if (travellerType === SingleParentFamily) {
    //   if (Number(form.value4) < 18) {
    //     errorState = true;
    //   } else if (Number(form.value4) > 17 && Number(form.value4) < 21) {
    //     if (form.fullTimeEducation) {
    //       errorState = true;
    //     }
    //   }
    // }
  }
  return errorState;
}

export function validateDateOrganiser(
  form: FormDataType,
  travellerType: string,
  dobMinAge?: string,
  dobMaxAge?: string,
  dobMaxLimit?: string
) {
  const errors: string[] = [];

  if (form.dateValue && isValidDate(form.dateValue)) {
    const date2 = form.dateValue && stripTimeFromDate(form.dateValue);
    const birthDate = dayjs(date2);
    const currentDate = dayjs();
    const age = currentDate.diff(birthDate, "year");

    if (age < 0) {
      errors.push(dobMinAge || "");
    }
    if (age > ageLimitOrganizer.max) {
      errors.push(dobMaxAge as string);
    }

    const enterdAge = Number(form.value4);
    if (enterdAge < 0) {
      errors.push(dobMinAge || "");
    }
    if (enterdAge < ageLimitOrganizer.min) {
      errors.push(dobMaxLimit || "");
    }
    if (enterdAge > ageLimitOrganizer.max) {
      errors.push(dobMaxAge || "");
    }
  } else {
    errors.push("Invalid Date of Birth");
  }

  // if (travellerType === Family || travellerType === SingleParentFamily) {
  //   const errorMsgFamily =
  //     ageFamilyFullTimeEducation ||
  //     "A family must be 2 adults plus any number of children under 18, or under 21 if in full time education";
  //   const errorMsgFamilySingle =
  //     ageSingleParentFullTimeEducation ||
  //     "A family must be 1 adults plus any number of children under 18, or under 21 if in full time education";

  //   const errorMsg =
  //     travellerType === Family ? errorMsgFamily : errorMsgFamilySingle;
  //   if (form.fullTimeEducation) {
  //     errors.push(errorMsg);
  //   }
  // }

  return errors;
}

// Regex patterns for Spanish identification types
export const identificationRegex = {
  DNI: /^[0-9]{8}[A-Za-z]$/, // 8 digits + 1 letter (e.g., 12345678X)
  NIE: /^[A-Za-z][0-9]{7}[A-Za-z]$/, // letter + 7 digits + letter (e.g., X1234567Z)
  PASSPORT: /^[A-Za-z0-9]{5,12}$/, // 5-12 alphanumeric characters
};

export function validateIdentificationNumber(
  identificationType: string | undefined,
  identificationNumber: string,
  errorMessage: string
): string {
  if (!identificationType || !identificationNumber?.trim()) {
    return "";
  }

  const regex =
    identificationRegex[identificationType as keyof typeof identificationRegex];
  if (!regex) return "";

  return regexValidaton(identificationNumber, regex) ? errorMessage : "";
}

export function getIdentificationFieldStatus(
  identificationType: string | undefined,
  identificationNumber: string | undefined,
  isFocused: boolean,
  errorMessage: string
): "success" | "error" | "warning" | null {
  if (!isFocused) return null;

  const error = validateIdentificationNumber(
    identificationType,
    identificationNumber || "",
    errorMessage
  );
  if (error) return "error";

  if (identificationNumber?.trim()) return "success";

  return "warning";
}

// Regex for input fields
const inputAddressRegex =
  /^([-''()_,.|`'’"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]+)+([-''()_,./`'’"º°ª\&\s]*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]*)*$/;
// const inputTownRegex =
//   /^([-''()_,.|`\\"\\&](\s)*)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF]+((\s)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff0-9\u1E00-\u1EFF-''()_,./`|\\"\\&]+)*$/;
const inputTownRegex =
  /^([-''()_,.|`'’\\"\\&]*\s*)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff\u1E00-\u1EFF0-9]+((\s)*[a-zA-Z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff\u1E00-\u1EFF0-9-''()_,./`'’|\\"\\&]*)*$/;

const inputPostCodeRegex = (countryFrom: string) => {
  switch (countryFrom) {
    case "GBR":
    case "GB":
      return /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\s?[0-9][A-Za-z]{2}$/;
    case "FRA":
    case "FR":
      return /^\d{5}$/;
    case "ESP":
    case "ES":
      return /^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/;
    case "PRT":
    case "PT":
      return /^(\d{4} \d{3}|\d{4}|\d{7})$/;
    case "IRL":
    case "IR":
      /**
       * Full Eircodes with both parts: "A65 B2CD"
       * Just Routing Keys: "A65"
       * Empty strings: ""
       */
      return /^(?:[A-Za-z0-9]{3}(?: ?[A-Za-z0-9]{4})?|)$/;
    default:
      return /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\s?[0-9][A-Za-z]{2}$/;
  }
};

export const regexValidaton = (input: string, regex: RegExp) => {
  return !regex.test(input);
};

export const inputCharacterCount = (input: string, length: number) =>
  input.length > length;

export function isFormDataCompleteOrganiser(
  formData: FormDataTypeOrganiser,
  travellerType?: string,
  wizard?: WizardDefinition,
  current?: StepDef,
  experimentDetails?: {
    deferOrganiserDetailsFlow?: string | null;
  } | null
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let error: any[] = [];

  if (
    MEDICAL_FLOW_V2_ENABLED === "true" &&
    travellerType === Individual &&
    formData.travelling &&
    formData.hasMedicalConditions !== false
  ) {
    return false;
  }

  if (
    !formData.value1 ||
    !formData.value2 ||
    !formData.value3 ||
    (!formData.value4 &&
      (formData.dateValue === undefined || !formData.dateValue)) ||
    (isAvailableNationalityField && !formData.nationalityValue)
  ) {
    return false;
  }

  // Check if user is in Spain hub A/B test variant
  const isPersonalDetailsDisabled =
    hidePersonalDetailsFields(experimentDetails);

  // Check identification fields for Spain only (skip if in variant)
  if (
    !isPersonalDetailsDisabled &&
    current?.brandSpecificParams?.showIdentificationFields &&
    (!formData.identificationType || !formData.identificationNumber)
  ) {
    return false;
  }

  // Skip address validation if in Spain hub variant
  if (!isPersonalDetailsDisabled) {
    if (
      formData.travelling === undefined ||
      (!current?.brandSpecificParams?.postCodeOptional && !formData.postCode) ||
      !formData.firstLineOfAddress ||
      !formData.city ||
      !formData.country
    ) {
      return false;
    }
  }
  // For Spain variant, only check travelling status
  if (isPersonalDetailsDisabled && formData.travelling === undefined) {
    return false;
  }

  if (formData.dateValue === undefined) {
    return false;
  }

  if (formData.value2) {
    const error1 = validateName(
      formData.value2,
      undefined,
      undefined,
      undefined,
      wizard
    );
    error = [...error, ...error1];
  }
  if (formData.value3) {
    const error1 = validateName(
      formData.value3,
      undefined,
      undefined,
      undefined,
      wizard
    );
    error = [...error, ...error1];
  }
  if (formData.value4) {
    const error1 = validateDateOrganiser(formData, travellerType || Individual);
    error = [...error, ...error1];
  }

  // Skip address and identification field validation if in Spain hub variant
  if (!isPersonalDetailsDisabled) {
    if (!formData.firstLineOfAddress) return false;
    if (regexValidaton(formData.firstLineOfAddress, inputAddressRegex)) {
      return false;
    }

    if (
      formData.secondLineOfAddress &&
      regexValidaton(formData.secondLineOfAddress, inputAddressRegex)
    ) {
      return false;
    }

    if (!formData.city) return false;
    if (regexValidaton(formData.city, inputTownRegex)) {
      return false;
    }

    if (!formData.postCode && !current?.brandSpecificParams?.postCodeOptional)
      return false;
    if (
      !current?.brandSpecificParams?.postCodeOptional &&
      (regexValidaton(
        formData.postCode,
        inputPostCodeRegex(formData.country)
      ) ||
        inputCharacterCount(formData.postCode, 8))
    ) {
      return false;
    }

    // Validate identification number format
    if (
      current?.brandSpecificParams?.showIdentificationFields &&
      formData.identificationType &&
      formData.identificationNumber
    ) {
      const idError = validateIdentificationNumber(
        formData.identificationType,
        formData.identificationNumber,
        "Invalid"
      );
      if (idError) return false;
    }
  }

  if (error.length > 0) {
    return false;
  }

  return true;
}

// Validation for input fields

export const checkValidation = (
  input: string,
  countryFrom: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any
) => {
  switch (input) {
    case "line1":
      return [
        {
          errorType: "regex",
          validation: inputAddressRegex,
          errorMsg: props?.addressLineOneErrorMasg ?? "Input Error",
        },
        {
          errorType: "maxTextCount",
          validation: MAX_ADDRESS_LENGTH,
          errorMsg: props?.addressLineOneErrorMasg ?? "Input Error",
        },
      ];
    case "line2":
      return [
        {
          errorType: "regex",
          validation: inputAddressRegex,
          errorMsg: props?.addressLineTwoErrorMasg ?? "Input Error",
        },
        {
          errorType: "maxTextCount",
          validation: MAX_ADDRESS_LENGTH,
          errorMsg: props?.addressLineTwoErrorMasg ?? "Input Error",
        },
      ];
    case "city":
      return [
        {
          errorType: "regex",
          validation: inputTownRegex,
          errorMsg: props?.TownCityErrorMasg ?? "Input Error",
        },
        {
          errorType: "maxTextCount",
          validation: 40,
          errorMsg: props?.TownCityErrorMasg ?? "Input Error",
        },
      ];
    case "postalCode":
      return [
        {
          errorType: "regex",
          validation: inputPostCodeRegex(countryFrom),
          errorMsg: props?.postcodeErrorMasg ?? "Input Error",
        },
        {
          errorType: "maxTextCount",
          validation: 8,
          errorMsg: props?.postcodeErrorMasg ?? "Input Error",
        },
      ];
    default:
      return [];
  }
};

export const ifAddressData = (
  addressData: AddressType[],
  current?: StepDef
) => {
  if (
    addressData.length > 0 &&
    addressData[0]?.attributes &&
    addressData[0].attributes.line1 &&
    (addressData[0].attributes.city || allowTownFieldInAddress) && // Some address in EXP doesnt have town
    addressData[0].attributes.countryIso2 &&
    (addressData[0].attributes.postalCode ||
      current?.brandSpecificParams?.postCodeOptional) // Some address in Int countries doesn't have postcode
  ) {
    return true;
  }
  return false;
};

export function modifyOrganiserForm(
  organizer: Organiser,
  policyStartDate?: string | null,
  getAgeFromPolicyStartForAMT?: boolean,
  policyType?: string
) {
  const form: FormDataTypeOrganiser = {
    value1: organizer.title,
    value2: organizer.firstName,
    value3: organizer.lastName,
    // nationalityValue: organizer.nationality,
    nationalityValue: organizer.nationalityId,
    id: 1,
    dateValue: organizer.dateOfBirth
      ? new Date(organizer?.dateOfBirth)
      : undefined,
    isDateCalenter: true,
    value4: "",
    travelling: organizer.travelling,
    fullTimeEducation: organizer.fullTimeEducation,
    postCode: organizer.postCode ?? "",
    firstLineOfAddress: organizer.firstLineOfAddress ?? "",
    secondLineOfAddress: organizer.secondLineOfAddress,
    city: organizer.city ?? "",
    country: organizer.country,
    telephone: organizer?.telephone,
    email: organizer?.email,
    identificationType: organizer.identificationType,
    identificationNumber: organizer.identificationNumber,
  };

  if (organizer.dateOfBirth) {
    form.dateValue = new Date(organizer.dateOfBirth);
    form.isDateCalenter = true;
    const birthDate = dayjs(form.dateValue).startOf("day");
    const currentDate = dayjs().startOf("day");
    const standardAge = currentDate.diff(birthDate, "year");

    const policyStart = policyStartDate
      ? dayjs(policyStartDate).startOf("day")
      : null;
    const AMTAge = policyStart?.diff(birthDate, "year");

    const isAMT = policyType === ANNUAL_MULTI_TRIP;
    const age =
      getAgeFromPolicyStartForAMT && isAMT
        ? AMTAge ?? standardAge
        : standardAge;

    form.value4 = age.toString();
  }

  return form;
}

export const modifyOrganiserTraveller = (
  traveller: Traveller[],
  organiserTravellerId?: string
) => {
  let travellerEdit: Traveller[] = [...(traveller || [])];
  if (travellerEdit && travellerEdit.length > 0) {
    const travellingOrganiser = travellerEdit.find(
      (obj) => obj?.sequence === 1
    );

    if (
      organiserTravellerId === travellingOrganiser?.encryptedId ||
      !organiserTravellerId
    ) {
      sessionStorage.setItem(
        "organiserTravellerId",
        travellingOrganiser?.encryptedId || ""
      );
      if (travellerEdit && travellerEdit.length === 1) {
        travellerEdit = [];
      } else if (travellerEdit && travellerEdit.length > 1) {
        const indexToRemove = travellerEdit.findIndex(
          (obj) => obj?.sequence === 1
        );
        if (indexToRemove > -1) {
          // only splice array when item is found

          travellerEdit?.splice(indexToRemove, 1); // 2nd parameter means remove one item only
        }
      }
    }
  }
  return travellerEdit;
};

export function getSelectedSupportContent(
  selectedPartyType: string | number | undefined,
  props: OrganiserContent,
  isOrganiserTravelling: boolean | undefined,
  hasMedicalCondition?: boolean | undefined
) {
  let supportSectionContent: {
    drawerContent: object | null | undefined;
    drawerEnable: boolean | undefined;
    rightContent: object | null | undefined;
    loadMoreButtonText: string;
    chatButtonLabel?: string;
  } = {
    drawerContent: null,
    drawerEnable: false,
    rightContent: null,
    loadMoreButtonText: "",
    chatButtonLabel: "",
  };

  const isIndividualOrganizerTravelling: boolean = !!(
    isOrganiserTravelling && selectedPartyType === Individual
  );

  if (isOrganiserTravelling === undefined) {
    supportSectionContent = {
      drawerContent: props.defaultSupportDrawer,
      drawerEnable: props.defaultLoadMoreEnable,
      rightContent: props.assistantDescription,
      loadMoreButtonText: props.defaultLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }
  if (isIndividualOrganizerTravelling) {
    if (MEDICAL_FLOW_V2_ENABLED !== "true") {
      supportSectionContent = {
        drawerContent: props.travellingYesSupportDrawer,
        drawerEnable: props.travellingYesLoadMoreEnable,
        rightContent: props.travellingIndividualYesSupportSection,
        loadMoreButtonText: props.travellingYesLoadMoreButtonText,
        chatButtonLabel: props.chatButtonLabel,
      };
    } else {
      if (hasMedicalCondition === undefined) {
        supportSectionContent = {
          drawerContent: props.travellingYesSupportDrawer,
          drawerEnable: props.travellingYesLoadMoreEnable,
          rightContent: props.travellingIndividualYesSupportSection,
          loadMoreButtonText: props.travellingYesLoadMoreButtonText,
          chatButtonLabel: props.chatButtonLabel,
        };
      }
      if (hasMedicalCondition === true) {
        supportSectionContent = {
          drawerContent: props.withMedicalConditionSupportDrawer,
          drawerEnable: props.withMedicalConditionLoadMoreEnable,
          rightContent: props.withOrganiserMedicalConditionSupportSection,
          loadMoreButtonText: props.withMedicalConditionLoadMoreButtonText,
          chatButtonLabel: props.chatButtonLabel,
        };
      }
      if (hasMedicalCondition === false) {
        supportSectionContent = {
          drawerContent: props.withoutMedicalConditionSupportDrawer,
          drawerEnable: props.withoutMedicalConditionLoadMoreEnable,
          rightContent: props.withoutMedicalConditionSupportSection,
          loadMoreButtonText: props.withoutMedicalConditionLoadMoreButtonText,
          chatButtonLabel: props.chatButtonLabel,
        };
      }
    }
  }

  if (isOrganiserTravelling === false && selectedPartyType === Individual) {
    supportSectionContent = {
      drawerContent: props.travellingNoSupportDrawer,
      drawerEnable: props.travellingNoLoadMoreEnable,
      rightContent: props.travellingIndividualNoSupportSection,
      loadMoreButtonText: props.travellingNoLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (isOrganiserTravelling && selectedPartyType === Couple) {
    supportSectionContent = {
      drawerContent: props.travellingYesSupportDrawer,
      drawerEnable: props.travellingYesLoadMoreEnable,
      rightContent: props.travellingCoupleYesSupportSection,
      loadMoreButtonText: props.travellingYesLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (isOrganiserTravelling === false && selectedPartyType === Couple) {
    supportSectionContent = {
      drawerContent: props.travellingNoSupportDrawer,
      drawerEnable: props.travellingNoLoadMoreEnable,
      rightContent: props.travellingCoupleNoSupportSection,
      loadMoreButtonText: props.travellingNoLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (
    isOrganiserTravelling &&
    (selectedPartyType === Family ||
      selectedPartyType === "SINGLE_PARENT_FAMILY")
  ) {
    supportSectionContent = {
      drawerContent: props.travellingYesSupportDrawer,
      drawerEnable: props.travellingYesLoadMoreEnable,
      rightContent: props.travellingFamilyYesSupportSection,
      loadMoreButtonText: props.travellingYesLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (
    isOrganiserTravelling === false &&
    (selectedPartyType === Family ||
      selectedPartyType === "SINGLE_PARENT_FAMILY")
  ) {
    supportSectionContent = {
      drawerContent: props.travellingNoSupportDrawer,
      drawerEnable: props.travellingNoLoadMoreEnable,
      rightContent: props.travellingFamilyNoSupportSection,
      loadMoreButtonText: props.travellingNoLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (isOrganiserTravelling && selectedPartyType === Group) {
    supportSectionContent = {
      drawerContent: props.travellingYesSupportDrawer,
      drawerEnable: props.travellingYesLoadMoreEnable,
      rightContent: props.travellingOtherYesSupportSection,
      loadMoreButtonText: props.travellingYesLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }

  if (isOrganiserTravelling === false && selectedPartyType === Group) {
    supportSectionContent = {
      drawerContent: props.travellingNoSupportDrawer,
      drawerEnable: props.travellingNoLoadMoreEnable,
      rightContent: props.travellingOtherNoSupportSection,
      loadMoreButtonText: props.travellingNoLoadMoreButtonText,
      chatButtonLabel: props.chatButtonLabel,
    };
  }
  return supportSectionContent;
}

const strokeConfig = [
  {
    brand: "staysure",
    strokeColorCode: "#272427",
  },
  {
    brand: "avanti",
    strokeColorCode: "#2E2D2C",
  },
];

export function getStrokeColor(lBrand: string | undefined): string {
  let strokeColor = "";
  strokeConfig.forEach((config) => {
    if (config.brand === lBrand) {
      strokeColor = `${config.strokeColorCode}`;
    }
  });
  return strokeColor;
}

export const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

export function assignIfEmpty(target: string, value: string) {
  return target || value;
}

export function checkAndAssign(
  errObj: { count: number; firstError: string },
  condition: boolean,
  field: string
): { count: number; firstError: string } {
  if (condition) {
    return {
      count: errObj.count + 1,
      firstError: assignIfEmpty(errObj.firstError, field),
    };
  }
  return errObj;
}

interface GetErrorDetailsData {
  showErrors: boolean;
  errorStatus: {
    titleStatus: string | null;
    firstNameStatus: string | null;
    lastNameStatus: string | null;
    identificationTypeStatus: string | null;
    identificationNumberStatus: string | null;
    postCodeStatus: string | null;
    addressLine1Status: string | null;
    addressLine2Status: string | null;
    addressCityStatus: string | null;
    addressPostalCodeStatus: string | null;
    dobStatus: string | null;
    nationalityStatus: string | null;
  };
  additionalData: boolean;
  addressDetails: string;
  travelling: boolean | undefined;
  hasMedicalConditions: boolean | undefined;
  newMedicalFlowEnabled: boolean | undefined;
  travellerType: string | undefined;
  experimentDetails?: {
    deferOrganiserDetailsFlow?: string | null;
  } | null;
}

export function getErrorDetailsQuote(data: GetErrorDetailsData) {
  const {
    showErrors,
    errorStatus,
    additionalData,
    addressDetails,
    travelling,
    hasMedicalConditions,
    newMedicalFlowEnabled,
    travellerType,
    experimentDetails,
  } = data;

  const {
    titleStatus,
    firstNameStatus,
    lastNameStatus,
    identificationTypeStatus,
    identificationNumberStatus,
    postCodeStatus,
    addressLine1Status,
    addressLine2Status,
    addressCityStatus,
    addressPostalCodeStatus,
    dobStatus,
    nationalityStatus,
  } = errorStatus;

  let errObj = { count: 0, firstError: "" };

  if (!showErrors) return errObj;

  // Check if user is in Spain hub A/B test variant
  const isPersonalDetailsDisabled =
    hidePersonalDetailsFields(experimentDetails);

  // Shared field checks
  errObj = checkAndAssign(errObj, titleStatus === "warning", "value1");
  errObj = checkAndAssign(
    errObj,
    firstNameStatus === "warning" || firstNameStatus === "error",
    "value2"
  );
  errObj = checkAndAssign(
    errObj,
    lastNameStatus === "warning" || lastNameStatus === "error",
    "value3"
  );

  // Address checks (skip if in Spain hub variant)
  if (!isPersonalDetailsDisabled) {
    if (!additionalData && !addressDetails) {
      errObj = checkAndAssign(
        errObj,
        postCodeStatus === "warning" || postCodeStatus === "error",
        "postCode"
      );
    } else {
      errObj = checkAndAssign(
        errObj,
        addressLine1Status === "warning" || addressLine1Status === "error",
        "line1"
      );
      errObj = checkAndAssign(errObj, addressLine2Status === "error", "line2");
      errObj = checkAndAssign(
        errObj,
        addressCityStatus === "warning" || addressCityStatus === "error",
        "city"
      );
      errObj = checkAndAssign(
        errObj,
        addressPostalCodeStatus === "warning" ||
          addressPostalCodeStatus === "error",
        "postalCode"
      );
    }

    // Identification checks (skip if in Spain hub variant)
    errObj = checkAndAssign(
      errObj,
      identificationTypeStatus === "warning",
      "identificationType"
    );
    errObj = checkAndAssign(
      errObj,
      identificationNumberStatus === "warning" ||
        identificationNumberStatus === "error",
      "identificationNumber"
    );
  }

  // Date of Birth and Nationality checks
  errObj = checkAndAssign(
    errObj,
    dobStatus === "warning" || dobStatus === "error",
    "dateValue"
  );
  errObj = checkAndAssign(
    errObj,
    nationalityStatus === "warning" && isAvailableNationalityField,
    "nationalityValue"
  );

  // Travel and Medical condition checks
  errObj = checkAndAssign(errObj, travelling === undefined, "travelling");
  const needsMedicalCheck =
    hasMedicalConditions === undefined &&
    travelling === true &&
    newMedicalFlowEnabled &&
    travellerType === Individual;
  errObj = checkAndAssign(errObj, !!needsMedicalCheck, "hasMedicalConditions");

  return errObj;
}

export function getErrorDetailsRenewals(data: GetErrorDetailsData) {
  const {
    showErrors,
    errorStatus,
    additionalData,
    addressDetails,
    travelling,
    hasMedicalConditions,
    newMedicalFlowEnabled,
    travellerType,
    experimentDetails,
  } = data;

  const {
    titleStatus,
    firstNameStatus,
    lastNameStatus,
    // identificationTypeStatus,
    // identificationNumberStatus,
    postCodeStatus,
    addressLine1Status,
    addressLine2Status,
    addressCityStatus,
    addressPostalCodeStatus,
    dobStatus,
    nationalityStatus,
  } = errorStatus;

  let errObj = { count: 0, firstError: "" };

  if (!showErrors) return errObj;

  // Check if user is in Spain hub A/B test variant
  const isPersonalDetailsDisabled =
    hidePersonalDetailsFields(experimentDetails);

  // Title check uses direct assignment, not assignIfEmpty
  errObj = checkAndAssign(errObj, titleStatus === "warning", "value1");

  // Common field checks
  errObj = checkAndAssign(
    errObj,
    firstNameStatus === "warning" || firstNameStatus === "error",
    "value2"
  );
  errObj = checkAndAssign(
    errObj,
    lastNameStatus === "warning" || lastNameStatus === "error",
    "value3"
  );
  errObj = checkAndAssign(
    errObj,
    nationalityStatus === "warning" && isAvailableNationalityField,
    "nationalityValue"
  );
  errObj = checkAndAssign(
    errObj,
    dobStatus === "warning" || dobStatus === "error",
    "dateValue"
  );

  // Address checks (skip if in Spain hub variant)
  if (!isPersonalDetailsDisabled) {
    if (!additionalData && !addressDetails) {
      errObj = checkAndAssign(
        errObj,
        postCodeStatus === "warning" || postCodeStatus === "error",
        "postCode"
      );
    } else {
      errObj = checkAndAssign(
        errObj,
        addressLine1Status === "warning" || addressLine1Status === "error",
        "line1"
      );
      errObj = checkAndAssign(errObj, addressLine2Status === "error", "line2");
      errObj = checkAndAssign(
        errObj,
        addressCityStatus === "warning" || addressCityStatus === "error",
        "city"
      );
      errObj = checkAndAssign(
        errObj,
        addressPostalCodeStatus === "warning" ||
          addressPostalCodeStatus === "error",
        "postalCode"
      );
    }
  }

  // Travel and medical checks
  errObj = checkAndAssign(errObj, travelling === undefined, "travelling");
  const needsMedicalCheck =
    hasMedicalConditions === undefined &&
    travelling === true &&
    newMedicalFlowEnabled &&
    travellerType === Individual;
  errObj = checkAndAssign(errObj, !!needsMedicalCheck, "hasMedicalConditions");

  return errObj;
}

export function buildFocusedFieldMap(application: string) {
  const baseFields = ["value1", "value2", "value3"];
  const addressFields = ["line1", "city", "postCode", "postalCode"];
  const dobAndNationality = ["nationalityValue", "dateValue"];
  const identificationFields = ["identificationType", "identificationNumber"];

  const fieldOrder =
    application === "quote"
      ? [
          ...baseFields,
          ...addressFields,
          ...dobAndNationality,
          ...identificationFields,
        ]
      : [...baseFields, ...dobAndNationality, ...addressFields];

  const fieldMap: Record<string, string[]> = {};
  fieldOrder.forEach((key, index) => {
    fieldMap[key] = fieldOrder.slice(0, index + 1);
  });

  const lastFieldKey = application === "quote" ? "dateValue" : "postalCode";
  fieldMap.travelling = fieldMap[lastFieldKey];
  fieldMap.hasMedicalConditions = fieldMap[lastFieldKey];

  return fieldMap;
}
